import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Alert } from "../components/Alert";
import { CopyButton } from "../components/CopyButton";
import { Spinner } from "../components/Spinner";
import { StepUpDialog } from "../components/StepUpDialog";
import { ApiError, createApiKey, listApiKeys, mfaChallenge, revokeApiKey } from "../lib/api";
import type { ApiKeyItem, CreatedApiKey } from "../lib/types";
import { useConfig } from "../state/config-context";
import { useSession } from "../state/session-context";

function formatDate(value: string | null, locale: string): string {
  if (value === null || value === "") {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

export function ApiKeysPage(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canRead = hasPermission("core.api_key.read");
  const canManage = hasPermission("core.api_key.manage");

  const [items, setItems] = useState<ApiKeyItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [scopesText, setScopesText] = useState("");
  const [expiresText, setExpiresText] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [pending, setPending] = useState<(() => Promise<void>) | null>(null);
  const [challengeError, setChallengeError] = useState<string | null>(null);
  const [challengeBusy, setChallengeBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await listApiKeys();
      setItems(data.items);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canRead) {
      void load();
    }
  }, [canRead, load]);

  const runAction = useCallback(
    async (action: () => Promise<void>): Promise<void> => {
      setError(null);
      try {
        await action();
      } catch (caught) {
        if (caught instanceof ApiError && caught.code === "mfa_required") {
          setChallengeError(null);
          setPending(() => action);
          return;
        }
        setError(caught instanceof ApiError ? caught.detail : t("error.network"));
      }
    },
    [t],
  );

  if (!canRead) {
    return <Navigate to="/" replace />;
  }

  const onCreate = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const scopes = scopesText
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value !== "");
    const parsedExpires = Number.parseInt(expiresText, 10);
    const expiresInDays =
      expiresText.trim() === "" || Number.isNaN(parsedExpires) ? null : parsedExpires;

    await runAction(async () => {
      setCreating(true);
      try {
        const key = await createApiKey({ name: name.trim(), scopes, expiresInDays });
        setCreated(key);
        setName("");
        setScopesText("");
        setExpiresText("");
        await load();
      } finally {
        setCreating(false);
      }
    });
  };

  const onRevoke = async (id: string): Promise<void> => {
    await runAction(async () => {
      await revokeApiKey(id);
      setConfirmId(null);
      await load();
    });
  };

  const onChallengeSubmit = async (code: string): Promise<void> => {
    setChallengeBusy(true);
    setChallengeError(null);
    try {
      await mfaChallenge(code);
      const action = pending;
      setPending(null);
      if (action !== null) {
        await runAction(action);
      }
    } catch (caught) {
      setChallengeError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setChallengeBusy(false);
    }
  };

  const locale = lang === "es" ? "es-ES" : "en-US";

  return (
    <section className="page" aria-labelledby="api-keys-title">
      <header className="page__header">
        <h1 id="api-keys-title">{t("apiKeys.title")}</h1>
        <p className="muted">{t("apiKeys.subtitle")}</p>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      {created !== null ? (
        <div className="key-reveal" role="status">
          <h2 className="key-reveal__title">{t("apiKeys.createdTitle")}</h2>
          <p className="muted">{t("apiKeys.createdHint")}</p>
          <div className="credential-block__row">
            <code className="break-all">{created.key}</code>
            <CopyButton value={created.key} />
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setCreated(null)}>
            {t("common.close")}
          </button>
        </div>
      ) : null}

      {canManage ? (
        <form className="form form--inline-card" onSubmit={(event) => void onCreate(event)}>
          <h2 className="card__title">{t("apiKeys.create")}</h2>
          <div className="form-grid">
            <label className="field" htmlFor="key-name">
              <span>{t("apiKeys.name")}</span>
              <input
                id="key-name"
                required
                maxLength={100}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="key-scopes">
              <span>{t("apiKeys.scopes")}</span>
              <input
                id="key-scopes"
                value={scopesText}
                aria-describedby="key-scopes-hint"
                onChange={(event) => setScopesText(event.target.value)}
              />
              <small id="key-scopes-hint" className="field__hint">
                {t("apiKeys.scopesHint")}
              </small>
            </label>
            <label className="field" htmlFor="key-expires">
              <span>{t("apiKeys.expires")}</span>
              <input
                id="key-expires"
                type="number"
                min={1}
                max={3650}
                value={expiresText}
                aria-describedby="key-expires-hint"
                onChange={(event) => setExpiresText(event.target.value)}
              />
              <small id="key-expires-hint" className="field__hint">
                {t("apiKeys.expiresHint")}
              </small>
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={creating}>
              {creating ? t("apiKeys.creating") : t("apiKeys.submit")}
            </button>
          </div>
        </form>
      ) : null}

      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items !== null && items.length === 0 ? (
        <p className="empty">{t("apiKeys.empty")}</p>
      ) : null}

      {!loading && items !== null && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("apiKeys.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("apiKeys.name")}</th>
                <th scope="col">{t("apiKeys.prefix")}</th>
                <th scope="col">{t("apiKeys.status")}</th>
                <th scope="col">{t("apiKeys.lastUsed")}</th>
                <th scope="col">{t("apiKeys.expiresAt")}</th>
                {canManage ? (
                  <th scope="col" className="table__actions-col">
                    <span className="sr-only">{t("apiKeys.revoke")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const revoked = item.revokedAt !== null;
                return (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>
                      <code>{item.prefix}</code>
                    </td>
                    <td>
                      <span className={`badge${revoked ? " badge--muted" : ""}`}>
                        {revoked ? t("apiKeys.revoked") : t("apiKeys.active")}
                      </span>
                    </td>
                    <td>{formatDate(item.lastUsedAt, locale)}</td>
                    <td>
                      {item.expiresAt === null
                        ? t("apiKeys.never")
                        : formatDate(item.expiresAt, locale)}
                    </td>
                    {canManage ? (
                      <td className="table__actions-col">
                        {revoked ? null : confirmId === item.id ? (
                          <span className="inline-actions">
                            <button
                              type="button"
                              className="btn btn--danger btn--sm"
                              onClick={() => void onRevoke(item.id)}
                            >
                              {t("apiKeys.revoke")}
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => setConfirmId(null)}
                            >
                              {t("common.cancel")}
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setConfirmId(item.id)}
                          >
                            {t("apiKeys.revoke")}
                          </button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <StepUpDialog
        open={pending !== null}
        busy={challengeBusy}
        error={challengeError}
        onCancel={() => {
          setPending(null);
          setChallengeError(null);
        }}
        onSubmit={(code) => void onChallengeSubmit(code)}
      />
    </section>
  );
}
